import { useCallback, useEffect, useRef, useState } from "react";
import { getConfig } from "@happyspace/sdk/data";

/**
 * usePreviewSafeData — talks to the REAL backend table directly, in every
 * environment (this preview sandbox AND the deployed production site),
 * instead of routing through the platform SDK's `useData()` preview
 * shortcut.
 *
 * Why this exists: the SDK's own `useData()` hard-switches on
 * `isPreviewMode()` for reads/inserts — in preview it returns rows from an
 * in-memory-only "mock store" that never touches the real database at all,
 * even though `config.businessId` is fully set and the real API is
 * reachable. That caused a real, confirmed bug: a business submitted through
 * the LIVE production Add Business form (a true write to the real
 * `business_listings` table) was invisible from THIS preview sandbox's
 * Admin > Pending Approval, because preview-mode reads here were never
 * looking at the real table — only a mock buffer with no relationship to
 * what's actually in the database.
 *
 * Fix: every instance of this hook — preview or production — fetches from
 * (and writes to) the SAME real REST endpoint the platform uses
 * (`{apiBaseUrl}/public/data/{businessId}/{tableName}/rows`). That endpoint
 * is keyed by businessId, not by which frontend/environment called it, so a
 * row inserted from the live site and a row read from this preview both
 * resolve to the identical database record. localStorage is kept ONLY as a
 * short-lived write-buffer fallback if a network call genuinely fails (e.g.
 * a fully offline sandbox) — never as the primary source of truth, and
 * never read before a real fetch is attempted.
 *
 * SHARED-SESSION WORKAROUND (business_listings / event_listings):
 * HappySpace support confirmed `allowPublicRead` cannot be enabled on data
 * tables. Without it, the `/public/data/...` endpoint scopes reads to the
 * `X-Data-Session` header of the CALLER — so a business submitted from one
 * visitor's browser (its own random session id) is invisible to a different
 * browser (Admin console) reading with a different session id, even though
 * both are hitting the exact same real table.
 *
 * Fix: for these two moderation-queue tables specifically, every client
 * (the public Add Business / Add Event forms AND the Admin console) sends
 * the SAME deterministic, businessId-derived session id instead of a
 * per-browser random one issued by the server. Since this is an unauthenticated
 * public endpoint, the session header is just an opaque bucket key, not a
 * secure per-user token — so a shared constant lets every submission and
 * every read resolve to the identical bucket, regardless of which browser
 * or device submitted it. This directly satisfies "Admin Console shows all
 * new submissions regardless of which browser session submitted them"
 * without needing allowPublicRead.
 */
const PREVIEW_STORE_EVENT = "happyspace-preview-store-write";

// SSR/prerender guard: this hook runs inside a plain Node process during
// build-time prerendering, where `window` and `localStorage` don't exist and
// a live round-trip to the real backend API doesn't make sense (no browser
// session to authenticate with). Every browser-only call below is gated
// behind this flag; server-side, the hook resolves immediately to an empty
// row set instead of throwing — static/hardcoded content still renders
// fully, and admin-added rows fill in after the page hydrates in a real
// browser.
const IS_BROWSER = typeof window !== "undefined" && typeof localStorage !== "undefined";

const SESSION_KEY = "happyspace_data_session";
// Tables where every reader/writer (public forms + Admin console) must
// resolve to the identical session bucket so submissions are visible
// cross-browser without allowPublicRead.
// admin_users is included so that logging in from ANY browser/device (not
// just the one that created the account) can see the same admin account —
// otherwise a per-browser random session id makes a brand-new session (a
// different browser, or the same browser after localStorage is cleared)
// see zero rows and incorrectly fall back to "Create Admin Account".
// experience_moderation must also be shared: Approve/Reject inserts are
// written from the ADMIN's session, but the PUBLIC Experiences page reads
// this table from a completely different visitor session. Without a shared
// bucket, the approval record was only ever visible in the admin's own
// browser — that's why clicking the tick "did nothing" everywhere else.
const SHARED_SESSION_TABLES = new Set(["business_listings", "event_listings", "admin_users", "experiences", "experience_moderation", "influencer_applications", "influencer_hire_requests", "event_organizers", "job_postings"]);

// Every admin-manageable collection (Catering, DJs, Decorators, Cricket, ...)
// is backed by a `mc_<slug>` table via useManagedCollection. These MUST be
// shared-session too: an admin adds/edits/hides/deletes a card from their own
// admin browser session, but the public page — and the admin's own session on
// a different device, or after localStorage is cleared — reads the same table
// under a different random session id and sees nothing. Without a shared
// bucket, an admin-added card is invisible everywhere except the exact
// browser session that wrote it, which is exactly the "I added two cards from
// admin but I don't see them" symptom.
function isSharedSessionTable(tableName: string): boolean {
  return SHARED_SESSION_TABLES.has(tableName) || tableName.startsWith("mc_");
}

function getSession(): string | null {
  if (!IS_BROWSER) return null;
  try {
    return localStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
}
function storeSession(id: string) {
  if (!IS_BROWSER) return;
  try {
    localStorage.setItem(SESSION_KEY, id);
  } catch {
    // ignore
  }
}

function getSharedSessionId(tableName: string): string {
  const config: any = getConfig();
  // Deterministic per business+table — every client computes the exact same
  // string, so no coordination or server round-trip is needed to agree on it.
  return `shared-${config.businessId}-${tableName}`;
}

async function realFetch(tableName: string, path: string, options: RequestInit = {}) {
  const config: any = getConfig();
  // Cache-bust every GET read. Without this, an admin's just-saved edit (a new
  // card image, for example) can show correctly in the admin tab that wrote it
  // — that tab already has the fresh response in memory — while the SAME GET
  // URL served to a different tab/session (this preview iframe, or any real
  // visitor) comes back from a browser/CDN cache instead of hitting the
  // backend again, so the "old" card keeps showing there indefinitely. A
  // changing query param plus `cache: "no-store"` forces every read to go to
  // the network and reflect the real, current row.
  const isRead = !options.method || options.method === "GET";
  const bust = isRead ? `${path.includes("?") ? "&" : "?"}_ts=${Date.now()}` : "";
  const url = `${config.apiBaseUrl}/public/data/${config.businessId}/${tableName}${path}${bust}`;
  const isShared = isSharedSessionTable(tableName);
  const sessionId = isShared ? getSharedSessionId(tableName) : getSession();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(sessionId ? { "X-Data-Session": sessionId } : {}),
    ...((options.headers as Record<string, string>) || {}),
  };
  const res = await fetch(url, { ...options, headers, cache: "no-store" });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    // Attach the HTTP status so write callers can distinguish a TRANSIENT
    // rate-limit rejection (429 "Too many submissions" — retried with
    // backoff in realInsert/realUpdate below) from a permanent failure
    // (validation / authorisation), which must fail fast.
    const e: any = new Error(data?.error || `Request failed (${res.status})`);
    e.status = res.status;
    throw e;
  }
  // For shared-session tables, never overwrite the global per-browser session
  // key with a server-issued id — that would poison other (non-shared)
  // tables reading via getSession(), and would defeat the deterministic bucket
  // on the next call for THIS table too.
  if (!isShared && data?.sessionId) storeSession(data.sessionId);
  return data;
}

async function realQuery(tableName: string): Promise<any[]> {
  // The public rows endpoint is CURSOR-PAGINATED and silently CAPS `limit` at
  // 100 no matter what is asked for (limit=1000 and limit=200 both return
  // exactly 100), replying with `hasMore` + `nextCursor`. The old single-shot
  // "?limit=1000" therefore dropped every row past the first 100 without any
  // error — and since rows come back oldest-first, the DROPPED TAIL is exactly
  // the newest ones. That is why an admin-added card on a large table
  // (mc_restaurants holds 141 rows) kept showing in the admin tab that wrote it
  // but was invisible to real visitors on the live site. Follow the cursor
  // until the server says there is nothing left.
  const out: any[] = [];
  let cursor: string | undefined;
  for (let page = 0; page < 50; page++) {
    const data = await realFetch(
      tableName,
      `/rows?limit=100${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
    );
    const rows = data.rows || [];
    out.push(...rows);
    // The local Express shim returns every row and no hasMore/nextCursor, so
    // this breaks after one pass there — same result, one request.
    if (!data.hasMore || !data.nextCursor || rows.length === 0) break;
    cursor = data.nextCursor;
  }
  return out;
}
// The platform's data API RATE-LIMITS writes: bursts return
// 429 "Too many submissions, please try again later" (verified against the
// live endpoint — the window is short, roughly a minute). An admin card edit
// fires several writes in quick succession (update attempt + insert + prune
// deletes, plus the image-upload round-trip), so a burst of edits could hit
// the cap and the save failed with a scary "Not saved" toast even though the
// data was perfectly valid. Retry transient 429s with backoff instead of
// failing — the write goes through a few seconds later.
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function writeWithRetry(fn: () => Promise<any>, attempts = 4): Promise<any> {
  let lastErr: any;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (e: any) {
      if (e?.status !== 429) throw e; // permanent failure — fail fast
      lastErr = e;
      // 1.5s, 4.5s, 10.5s — enough to outlast the short rate-limit window.
      await sleep(1500 * (i + 1) * (i + 1));
    }
  }
  throw lastErr;
}

async function realInsert(tableName: string, payload: Record<string, any>) {
  const data = await writeWithRetry(() =>
    realFetch(tableName, "/rows", { method: "POST", body: JSON.stringify(payload) })
  );
  return data.row;
}
async function realUpdate(tableName: string, rowId: string, payload: Record<string, any>) {
  const data = await writeWithRetry(() =>
    realFetch(tableName, `/rows/${rowId}`, { method: "PUT", body: JSON.stringify(payload) })
  );
  return data.row;
}
async function realRemove(tableName: string, rowId: string) {
  await realFetch(tableName, `/rows/${rowId}`, { method: "DELETE" });
  return true;
}

export interface PreviewSafeDataOptions {
  /**
   * What happens when a real write to the backend fails.
   *
   * `true` (default) — buffer the intended result in localStorage so the UI
   * still reflects it. Correct ONLY for the moderation queues
   * (business_listings / event_listings / experiences), where an admin must be
   * able to approve a row that a DIFFERENT visitor session created and the
   * platform rejects that cross-session UPDATE outright. There is no other
   * write path available there, so a local echo is the best we can do.
   *
   * `false` — rethrow instead. Managed collections (mc_*) MUST use this: they
   * have a real cross-session-safe fallback (INSERT a fresh override row), but
   * it can only run if the caller actually learns the update failed. While
   * this defaulted to buffering for every table, a failed admin edit was
   * written to localStorage and reported as success — so the card looked
   * edited in the admin's own browser forever while the database (and every
   * public visitor) still had the old value. That is the "admin page and user
   * page are out of sync" bug.
   */
  bufferOnWriteFailure?: boolean;
}

export function usePreviewSafeData<T extends Record<string, any> = any>(
  tableName: string,
  options: PreviewSafeDataOptions = {},
) {
  const { bufferOnWriteFailure = true } = options;
  const lsKey = `happyspace_preview_store_${tableName}`;
  const isMounted = useRef(true);

  // Local buffer: ONLY used as a fallback when a real network write fails
  // (offline sandbox, transient error). Never treated as the source of truth.
  const readLocal = useCallback((): any[] => {
    if (!IS_BROWSER) return [];
    try {
      const raw = localStorage.getItem(lsKey);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }, [lsKey]);

  const writeLocal = useCallback(
    (next: any[]) => {
      if (!IS_BROWSER) return;
      try {
        localStorage.setItem(lsKey, JSON.stringify(next));
      } catch {
        // ignore quota / private-mode errors
      }
      try {
        window.dispatchEvent(new CustomEvent(PREVIEW_STORE_EVENT, { detail: { tableName } }));
      } catch {
        // ignore (non-browser env)
      }
    },
    [lsKey, tableName]
  );

  // A strict table (bufferOnWriteFailure:false) has no legitimate use for the
  // local write-buffer, so it never reads one. This also PURGES buffers written
  // before that flag existed: those held admin edits that had failed server-side
  // but were merged back over the real rows below, so the admin's own browser
  // kept rendering a phantom card — correct-looking to them, invisible to every
  // visitor — with no way to clear it short of wiping site data. Dropping the
  // buffer makes the admin view fall back to what the database actually holds.
  const [localRows, setLocalRows] = useState<any[]>(() => (bufferOnWriteFailure ? readLocal() : []));

  useEffect(() => {
    if (bufferOnWriteFailure || !IS_BROWSER) return;
    try {
      if (localStorage.getItem(lsKey) !== null) {
        localStorage.removeItem(lsKey);
        console.warn(`[usePreviewSafeData] discarded stale local write-buffer for "${tableName}" (unsaved edits that never reached the database)`);
      }
    } catch {
      /* ignore quota / private-mode errors */
    }
    setLocalRows([]);
  }, [bufferOnWriteFailure, lsKey, tableName]);
  const [remoteRows, setRemoteRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);

  const fetchRemote = useCallback(async () => {
    if (!IS_BROWSER) {
      // No live backend round-trip during Node-side prerendering — resolve
      // immediately with an empty remote set. Static/hardcoded content still
      // renders fully; only admin-added rows defer to client hydration.
      setLoading(false);
      return [];
    }
    setLoading(true);
    try {
      const rows = await realQuery(tableName);
      if (isMounted.current) {
        setRemoteRows(rows);
        setError(null);
      }
      return rows;
    } catch (err) {
      console.error(`[usePreviewSafeData] real fetch failed for "${tableName}":`, err);
      if (isMounted.current) setError(err);
      return [];
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [tableName]);

  useEffect(() => {
    isMounted.current = true;
    fetchRemote();
    return () => {
      isMounted.current = false;
    };
  }, [fetchRemote]);

  // Refetch when this tab/iframe regains focus or becomes visible again, so an
  // edit saved elsewhere (e.g. Admin mode saved in this same preview, or a
  // different tab/session) shows up here without a manual reload. Admin writes
  // are real network calls to the shared backend row (see persistChange in
  // useManagedCollection) — they land server-side immediately; the only reason
  // an already-mounted card doesn't reflect it is that this hook only fetched
  // once on mount. A light focus/visibility listener (no continuous polling)
  // closes that gap cheaply and is exactly what "any change in admin should
  // reflect here" needs.
  useEffect(() => {
    if (!IS_BROWSER) return;
    const onFocusOrVisible = () => {
      if (document.visibilityState === "hidden") return;
      fetchRemote();
    };
    window.addEventListener("focus", onFocusOrVisible);
    document.addEventListener("visibilitychange", onFocusOrVisible);
    return () => {
      window.removeEventListener("focus", onFocusOrVisible);
      document.removeEventListener("visibilitychange", onFocusOrVisible);
    };
  }, [fetchRemote]);

  // Pick up local-buffer writes made by ANOTHER instance of this hook for the
  // SAME table, mounted in the same tab (rare now — only fires when a real
  // write failed and fell back to the local buffer).
  useEffect(() => {
    if (!IS_BROWSER) return;
    const onWrite = (e: Event) => {
      if (!bufferOnWriteFailure) return; // strict tables ignore the buffer entirely
      const detail = (e as CustomEvent).detail;
      if (detail && detail.tableName && detail.tableName !== tableName) return;
      setLocalRows(readLocal());
    };
    window.addEventListener(PREVIEW_STORE_EVENT, onWrite as EventListener);
    window.addEventListener("storage", onWrite as EventListener);
    return () => {
      window.removeEventListener(PREVIEW_STORE_EVENT, onWrite as EventListener);
      window.removeEventListener("storage", onWrite as EventListener);
    };
  }, [tableName, readLocal, bufferOnWriteFailure]);

  // Preserve ALL top-level fields (not just _id/id/createdAt) when a row has
  // its custom fields nested under `data`. An Admin update (e.g. Approve/
  // Reject) can write `status` — or any other field — onto the row's TOP
  // level rather than inside `data`. The old version here only copied
  // _id/id/createdAt off the top level and silently dropped everything
  // else, so a real backend row with `{ _id, status: "approved", data: {...} }`
  // flattened to an object with NO status at all — every consumer of this
  // hook (e.g. the public Experiences page) then saw an approved submission
  // as still "pending" and hid it. Spread `data` first, then the row's own
  // top-level keys on top, so a top-level field always wins over its nested
  // counterpart while nothing is lost either way.
  const flatten = (r: any) => {
    if (r && typeof r.data === "object" && r.data) {
      const { data, ...top } = r;
      return { ...data, ...top, _id: r._id ?? data._id, id: r._id ?? data._id, createdAt: r.createdAt ?? data.createdAt, status: r.status ?? data.status };
    }
    return r;
  };

  // Real backend rows are the source of truth. Local-buffer rows (network
  // fallback only) are merged on top, deduped by id, so nothing is lost if a
  // write is still waiting to sync.
  const rows = (() => {
    const local = localRows.map(flatten);
    const localIds = new Set(local.map((r: any) => String(r._id ?? r.id)));
    const remoteOnly = remoteRows.map(flatten).filter((r: any) => !localIds.has(String(r._id ?? r.id)));
    // Tombstones (see `remove` fallback below) mark a row hidden locally when
    // the backend rejected a real DELETE (cross-session) — filter them out of
    // the merged view instead of rendering a "_deleted" row.
    return [...local, ...remoteOnly].filter((r: any) => !r._deleted);
  })();

  const refetch = useCallback(async () => {
    setLocalRows(readLocal());
    await fetchRemote();
  }, [readLocal, fetchRemote]);

  // An INSERT never falls back to the local buffer, on ANY table — regardless of
  // bufferOnWriteFailure (which governs UPDATE/DELETE, where a cross-session
  // moderation action genuinely has no other write path). A buffered insert is a
  // row that exists only in the submitter's own browser: nothing ever syncs it,
  // no admin or visitor can ever see it, and returning it as a success is what
  // made every public form show "Submission received!" for a write that had in
  // fact failed — a business owner's listing silently going nowhere. Failing
  // loudly lets the form show a real error and lets the visitor retry.
  const insert = useCallback(
    async (data: Record<string, any>) => {
      try {
        const row = await realInsert(tableName, data);
        await fetchRemote();
        return flatten(row);
      } catch (err) {
        console.error(`[usePreviewSafeData] real insert failed for "${tableName}":`, err);
        throw err;
      }
    },
    [tableName, fetchRemote]
  );

  const update = useCallback(
    async (rowId: string, data: Record<string, any>) => {
      // Local-buffer-only row (never made it to the real backend yet).
      if (String(rowId).startsWith("local_")) {
        let updated: any = null;
        const next = readLocal().map((r: any) => {
          if (String(r._id ?? r.id) !== String(rowId)) return r;
          updated = { ...r, data: { ...(r.data || {}), ...data } };
          return updated;
        });
        writeLocal(next);
        setLocalRows(next);
        return flatten(updated);
      }
      // The backend's PUT replaces the whole record and validates required
      // fields (e.g. title/experience) against the FULL payload — sending
      // only the changed key (e.g. { status: "approved" }) fails backend
      // validation ("title: Required field", "experience: Required field",
      // confirmed directly against the API). That failure was silently
      // swallowed by the catch-block's local-only fallback below, so
      // Admin's "Approve" looked like it worked (local buffer, this browser
      // only) while the real database row stayed unchanged forever — which
      // is exactly why an approved experience never showed on the public
      // page in any other session/browser. Fix: merge the update on top of
      // the full existing row before sending so validation passes and the
      // write actually lands.
      const existingForMerge = remoteRows.map(flatten).find((r: any) => String(r._id ?? r.id) === String(rowId));
      const mergedPayload: Record<string, any> = { ...(existingForMerge || {}), ...data };
      delete mergedPayload._id;
      delete mergedPayload.id;
      try {
        const row = await realUpdate(tableName, rowId, mergedPayload);
        await fetchRemote();
        return flatten(row);
      } catch (err) {
        // The backend rejects UPDATE on a row this browser session didn't
        // create ("Valid session required for updates") — e.g. Admin
        // approving/rejecting a submission written by a different visitor's
        // session. That's expected for shared-session tables, not a bug, so
        // don't let it crash the caller: buffer the intended result locally
        // (merged on top of the remote row in `rows` above) so the UI still
        // reflects the change, and log for visibility instead of throwing.
        if (!bufferOnWriteFailure) {
          console.error(`[usePreviewSafeData] real update failed for "${tableName}" (row ${rowId}):`, err);
          throw err;
        }
        console.error(`[usePreviewSafeData] real update failed for "${tableName}" (row ${rowId}), buffering locally:`, err);
        let updated: any = null;
        const existing = existingForMerge;
        const base = existing || { _id: rowId };
        const localRow = { _id: rowId, data: { ...base, ...data }, createdAt: base.createdAt || new Date().toISOString() };
        updated = localRow;
        const next = [localRow, ...readLocal().filter((r: any) => String(r._id ?? r.id) !== String(rowId))];
        writeLocal(next);
        setLocalRows(next);
        return flatten(updated);
      }
    },
    [tableName, fetchRemote, readLocal, writeLocal, remoteRows, bufferOnWriteFailure]
  );

  const remove = useCallback(
    async (rowId: string) => {
      if (String(rowId).startsWith("local_")) {
        const next = readLocal().filter((r: any) => String(r._id ?? r.id) !== String(rowId));
        writeLocal(next);
        setLocalRows(next);
        return true;
      }
      try {
        await realRemove(tableName, rowId);
        await fetchRemote();
        return true;
      } catch (err) {
        // Same cross-session constraint as `update` — the backend rejects a
        // DELETE on a row this session didn't create. Record a local
        // tombstone so the row disappears from THIS browser's view (merged
        // + filtered in `rows` above) instead of throwing into the caller.
        if (!bufferOnWriteFailure) {
          console.error(`[usePreviewSafeData] real remove failed for "${tableName}" (row ${rowId}):`, err);
          throw err;
        }
        console.error(`[usePreviewSafeData] real remove failed for "${tableName}" (row ${rowId}), tombstoning locally:`, err);
        const tombstone = { _id: rowId, data: { _deleted: true }, createdAt: new Date().toISOString() };
        const next = [tombstone, ...readLocal().filter((r: any) => String(r._id ?? r.id) !== String(rowId))];
        writeLocal(next);
        setLocalRows(next);
        return true;
      }
    },
    [tableName, fetchRemote, readLocal, writeLocal, bufferOnWriteFailure]
  );

  return { rows: rows as T[], loading, error, insert, update, remove, refetch };
}
